import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import ProbabilitySection from './probabilitySection';

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

/**
 * ChallengeSeekingSection - Specialized component for displaying challenge seeking construct data
 *
 * This component provides a unique UI for the Challenge Seeking construct, featuring:
 * - Left side: Clustering-based bar chart with 3 categories (Selective, Agnostic, Seeking) summing to 100%
 * - Right side: Personalized passages with insights based on the user's category and 5 specific behavioral insights
 *
 * The component safely handles data extraction from Redux contextData and provides
 * fallback values for missing or invalid data.
 *
 * @returns {JSX.Element} The challenge seeking dashboard with bar chart and personalized content
 */
const ChallengeSeekingSection = () => {
  const { contextData } = useSelector((state) => state.studentPerformenceConstruct);

  const challengeSeekingData = useMemo(() => {
    if (!contextData) return null;

    const { construct_label = [] } = contextData;

    const selectiveLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('challengeseeking') &&
      item.behavior_graph_key?.toLowerCase().includes('selective')
    );

    const agnosticLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('challengeseeking') &&
      item.behavior_graph_key?.toLowerCase().includes('agnostic')
    );

    const seekingLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('challengeseeking') &&
      item.behavior_graph_key?.toLowerCase().includes('seeking')
    );

    if (!selectiveLabel && !agnosticLabel && !seekingLabel) {
      return null; 
    }

    const categories = [];

    if (selectiveLabel && typeof selectiveLabel.percentage !== 'undefined') {
      const selectivePercentage = Math.max(0, Math.min(100, safeNumber(selectiveLabel.percentage)));
      categories.push({ name: 'Selective', percentage: selectivePercentage, color: '#3b82f6' });
    }

    if (agnosticLabel && typeof agnosticLabel.percentage !== 'undefined') {
      const agnosticPercentage = Math.max(0, Math.min(100, safeNumber(agnosticLabel.percentage)));
      categories.push({ name: 'Agnostic', percentage: agnosticPercentage, color: '#f59e0b' });
    }

    if (seekingLabel && typeof seekingLabel.percentage !== 'undefined') {
      const seekingPercentage = Math.max(0, Math.min(100, safeNumber(seekingLabel.percentage)));
      categories.push({ name: 'Seeking', percentage: seekingPercentage, color: '#ef4444' });
    }

    if (categories.length === 0) {
      return null; 
    }

    const dominantCategory = categories.reduce((prev, current) =>
      (prev.percentage > current.percentage) ? prev : current
    );

    return {
      dominantCategory
    };
  }, [contextData]);

  const getPassageContent = (dominantCategory) => {
    switch (dominantCategory.name) {
      case 'Seeking':
        return {
          category: 'Challenge Seeking',
          color: '#ef4444',
          gamePassage: `You thrive on turning the pressure knob to eleven. Dense grids, rapid drops, steeper multipliers—these are invitations, not deterrents. From the outset you reach for settings that test reflexes, planning, and resilience all at once, knowing that higher stakes bring sharper focus and bigger pay-offs. The busier the screen and the tighter the timing, the more energized you become, treating each tough round as a proving ground for new techniques. Scores matter, but so does the satisfaction of mastering scenarios most players sidestep. Your pattern of steadily escalating difficulty signals a competitive spirit eager to explore limits, learn quickly from missteps, and convert hard-won experience into standout results.`,
          generalPassage: `You feel most alive when the odds tilt upward. Whether it's an ambitious deadline, a dense reading list, or an intricate design brief, you deliberately chase the scenarios that test the outer edges of your competence. High pressure sharpens your focus and accelerates learning, turning steep challenges into opportunities for rapid growth and standout results. Rather than waiting for comfort to catch up, you plunge ahead, refine your approach through quick feedback loops, and convert hard-earned insights into lasting expertise. Your track record shows that you don't just accept tough problems—you actively seek them out, confident that the biggest hurdles yield the greatest rewards.`
        };
      case 'Selective':
        return {
          category: 'Challenge Selective',
          color: '#3b82f6',
          gamePassage: `You approach difficulty like a sound engineer fine-tuning a mix—raising or lowering settings until the task feels "in the pocket." Easy modes rarely hold your attention, but you're equally wary of cranking everything to the max. Instead, you sample a level, gauge its demands, and make thoughtful tweaks so the experience stays engaging without becoming chaotic. When clutter builds or the tempo spikes, you dial things back just enough to keep control, then settle into a steady rhythm that lets your planning and accuracy shine. This calibrated style shows a strategic mind: you stretch your abilities, but always within a zone where focus and enjoyment remain high, turning each run into purposeful practice rather than a gamble.`,
          generalPassage: `You excel at sizing up a task and setting the difficulty dial to "productive stretch." When a project feels too simple you nudge the stakes upward, yet you're quick to rein things in if complexity starts to hamper clear thinking. This habit of fine-tuning conditions lets you stay focused on learning goals without slipping into frustration or complacency. Because you routinely aim for a sweet spot—challenging enough to spark growth, manageable enough to sustain momentum—you turn everyday assignments into deliberate practice sessions that steadily expand your skill set.`
        };
      case 'Agnostic':
        return {
          category: 'Challenge Agnostic',
          color: '#f59e0b',
          gamePassage: `You treat difficulty as background scenery—interesting, but never the main event. One session might drop you into a brisk, crowded puzzle; the next could be a slower, wide-open layout—and you roll with both. Because you seldom stop to adjust sliders or reorder options, each playthrough becomes a fresh sample of whatever the game serves up. Sometimes that means breezing through an accessible stage; other times you're thrust into something tougher without warning. Either way, you adapt on the fly, letting curiosity guide you more than any fixed preference for easy or hard. This flexible outlook keeps the experience varied and can surface hidden strengths, as you discover which kinds of challenges naturally spark your best performances.`,
          generalPassage: `You approach new situations with an open hand rather than a checklist, stepping into easy and demanding tasks alike with much the same curiosity. Instead of adjusting the environment first, you try it "as is," then improvise strategies to fit whatever difficulty you encounter. This willingness to accept varied levels of complexity builds adaptability: some days you cruise through straightforward work; on others you navigate steeper terrain without much pre-planning. By treating difficulty as just one of many variables, you discover fresh strengths and keep your experience portfolio broad—an outlook that prepares you for the unexpected twists common in real-world projects.`
        };
      default:
        return {
          category: 'Challenge Seeking Analysis',
          color: '#6b7280',
          gamePassage: 'Analysis of your challenge seeking patterns is being processed.',
          generalPassage: 'Analysis of your challenge seeking patterns is being processed.'
        };
    }
  };

  const getBehavioralInsights = (dominantCategory) => {
    switch (dominantCategory.name) {
      case 'Seeking':
        return [
          {
            title: 'Escalating Difficulty',
            description: 'You actively crank up difficulty sliders early and keep raising the bar as you go.'
          },
          {
            title: 'High-Intensity Preference',
            description: 'Dense layouts and rapid pace energise you; you look for the busiest, fastest options.'
          },
          {
            title: 'Risk-Reward Optimization',
            description: 'You chase big score multipliers, accepting higher risk in return for bigger pay-offs.'
          }
        ];
      case 'Selective':
        return [
          {
            title: 'Calibrated Challenge',
            description: 'You pick challenges that feel "just right," rarely pushing sliders or modifiers to extremes.'
          },
          {
            title: 'Controlled Environment',
            description: 'You keep the playfield tidy and the pace comfortable, favouring clarity over chaos.'
          },
          {
            title: 'Steady Performance',
            description: 'You aim for steady performance rather than high-risk, high-reward scores.'
          }
        ];
      case 'Agnostic':
        return [
          {
            title: 'Adaptive Acceptance',
            description: 'You dive into whatever setting is offered, showing little concern for whether it is easy or hard.'
          },
          {
            title: 'Natural Variation',
            description: 'Your session pace and screen clutter fluctuate, but you seldom fine-tune them.'
          },
          {
            title: 'Organic Scoring',
            description: 'Your score bonuses rise and fall naturally with the game; you rarely engineer them.'
          }
        ];
      default:
        return [];
    }
  };

  if (!challengeSeekingData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading challenge seeking data...</p>
        </div>
      </div>
    );
  }

  const { dominantCategory } = challengeSeekingData;
  const passageInfo = getPassageContent(dominantCategory);

  return (
    <div>
      {/* Keep existing Probability Section layout - DO NOT CHANGE */}
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 bg-blue-50 rounded-l-lg overflow-hidden">
          {/* Use the existing ProbabilitySection component - keeping it unchanged */}
          <ProbabilitySection />

          {/* Explanatory text below the chart */}
          <div className="px-8 py-6 border-t border-blue-100 rounded-br-lg rounded-bl-lg">
            <p className="text-sm sm:text-md md:text-lg text-gray-700 leading-relaxed">
              The probabilities for this model represent the confidence of the model that you belong with this overall group. These probabilities range from 0% (you are very unlikely to belong to the group) to 100% (you are very likely to belong to the group). Predictions have been scaled to sum to 100%, so if you receive scores of 40% for two groups, you are equally likely to be in either — the model isn’t sure which to pick between the two options.
            </p>
          </div>
        </div>

        {/* Right side - Personalized Passage */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-r-lg border-t border-r border-b border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 rounded mr-3" style={{ backgroundColor: passageInfo.color }}></div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold">{passageInfo.category}</h3>
          </div>

          {/* Your performance in the game */}
          <div className="mb-6">
            <h4 className="text-md sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 pb-2">Your performance in the game :</h4>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
              {passageInfo.gamePassage}
            </p>
          </div>

          {/* Your Problem Solving Style */}
          <div className="mb-6">
            <h4 className="text-md sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 pb-2">Your Problem Solving Style :</h4>
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
              {passageInfo.generalPassage}
            </p>
          </div>

          {/* Behavioral Observations */}
          <div>
            <h4 className="ttext-md sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 pb-4">Behavioral observations :</h4>
            <div className="space-y-3">
              {getBehavioralInsights(dominantCategory).map((insight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm md:text-lg text-gray-700 leading-relaxed">
                      <span className="font-medium">{insight.title}:</span> {insight.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeSeekingSection;
